import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  FormControl,
  TextArea,
  ThemeProvider,
} from '@alefeducation/ui-elements-library-core';
import Edit from '../common/Icons/Edit';
import { useStudentSubmissionDispatch } from './StudentSubmissionPageContext';
import { feedbackCharacterLimit } from './constants';

const FeedbackLabel = styled.div`
  display: flex;
  justify-content: flex-start;
  color: var(--river-bed);
  font-size: 16px;
  font-family: var(--font-family-signika);
  font-weight: 400;
  word-wrap: break-word;
`;
const OptionalSpan = styled.div`
  color: var(--primary-60);
  font-size: 16px;
  font-weight: 300;
  display: flex;
  justify-content: space-between;
  font-family: var(--font-family-signika);
  word-wrap: break-word;
`;

const FeedbackPreview = styled.div`
  font-size: 14px;
`;

const EditIcon = styled(Edit)`
  width: 24px;
  height: 24px;
  cursor: pointer;
`;

const LetterCount = styled.div`
  color: var(--primary-80);
  font-size: 12px;
  font-family: var(--font-family-signika);
  font-weight: 300;
  line-height: 16px;
  word-wrap: break-word;
  float: right;
`;

const Feedback = ({
  isReturned,
  teacherFeedback,
  setisFeedbackEditable,
  isFeedbackEditable,
  isSubmissionInProgress,
  isSubmitted,
  attachmentRequired,
  commentRequired,
  isActiveStudentInTheClass,
}) => {
  const { t } = useTranslation();
  const dispatch = useStudentSubmissionDispatch();
  const [letterCount, setLetterCount] = useState(0);

  const canGiveFeedback =
    !isFeedbackEditable &&
    !isReturned &&
    ((!attachmentRequired && !commentRequired) || isSubmitted);

  const canDisableFeedback =
    ((attachmentRequired || commentRequired) && !isSubmitted) ||
    isReturned ||
    isSubmissionInProgress;
  const feedbackChangeHandler = useCallback(event => {
    dispatch({ type: 'setFeedback', payload: event.target.value });
    setLetterCount(letterCount + 1);
  });

  const showFeedback = (isReturned && teacherFeedback) || !isReturned;
  const isFeedbackTextEditable =
    isFeedbackEditable && isActiveStudentInTheClass;
  return (
    showFeedback && (
      <ThemeProvider>
        <FormControl appearance="regular" style={{ flex: 1 }}>
          <FeedbackLabel>
            {t('Feedback')}
            &nbsp;
            <OptionalSpan>{t('(Optional)')}</OptionalSpan>
            {canGiveFeedback &&
              isActiveStudentInTheClass && (
                <div
                  data-testid={'feedback-edit'}
                  onClick={() => setisFeedbackEditable(true)}
                >
                  <EditIcon />
                </div>
              )}
          </FeedbackLabel>
          {isFeedbackTextEditable ? (
            <TextArea
              className="usprivacy"
              onBlur={e =>
                !e.target.value ? undefined : setisFeedbackEditable(false)
              }
              data-testid={'feedback-textarea'}
              onChange={feedbackChangeHandler.bind(this)}
              value={teacherFeedback}
              disabled={canDisableFeedback}
              maxLength={feedbackCharacterLimit}
              placeholder={'Type here'}
              rows={3}
            />
          ) : (
            <FeedbackPreview
              className="usprivacy"
              data-testid={'feedback-preview'}
            >
              {teacherFeedback}
            </FeedbackPreview>
          )}
          {isFeedbackTextEditable && (
            <LetterCount>
              {letterCount}/{feedbackCharacterLimit}
            </LetterCount>
          )}
        </FormControl>
      </ThemeProvider>
    )
  );
};

Feedback.defaultProps = {
  isActiveStudentInTheClass: true,
};

Feedback.propTypes = {
  isReturned: PropTypes.bool.isRequired,
  teacherFeedback: PropTypes.string.isRequired,
  setisFeedbackEditable: PropTypes.func.isRequired,
  isFeedbackEditable: PropTypes.bool.isRequired,
  isSubmissionInProgress: PropTypes.bool.isRequired,
  isSubmitted: PropTypes.bool.isRequired,
  attachmentRequired: PropTypes.bool.isRequired,
  commentRequired: PropTypes.bool.isRequired,
  isActiveStudentInTheClass: PropTypes.bool,
};

export default Feedback;
